import { ReactNode } from 'react'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { com } from '@eidu/web'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import { anchorTypeIdOf, isAllRowPermission, isGlobalRowPermission } from './utils'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import EntitySelectorPermission = com.eidu.sharedlib.entity.permission.EntitySelectorPermission
import EntityPathPermission = com.eidu.sharedlib.entity.permission.EntityPathPermission
import Action = com.eidu.sharedlib.entity.permission.Action
import Permission = com.eidu.sharedlib.entity.permission.Permission

export type PolicyMatch = { policyName: string; permission: Permission }

export const policyMatchesGlobalRow = (policies: ReadonlyArray<PermissionPolicyDTO>, action: Action): PolicyMatch[] =>
  policies.flatMap((p) =>
    [...p.policy.permissions.asJsReadonlySetView()]
      .filter((perm) => isGlobalRowPermission(perm, action))
      .map((permission) => ({ policyName: p.policy.name, permission }))
  )

export const policyMatchesEntityCell = (
  policies: ReadonlyArray<PermissionPolicyDTO>,
  entityTypes: readonly EntityType[],
  rowEntityTypeId: EntityTypeId,
  action: EntityAction
): PolicyMatch[] =>
  policies.flatMap((p) =>
    [...p.policy.permissions.asJsReadonlySetView()]
      .filter((perm) => {
        if (perm.action !== action) return false
        if (isAllRowPermission(perm, action)) return true
        const anchor = anchorTypeIdOf(perm, entityTypes)
        return anchor !== null && anchor.equals(rowEntityTypeId)
      })
      .map((permission) => ({ policyName: p.policy.name, permission }))
  )

const PolicyInfoBadge = ({ matches }: { matches: PolicyMatch[] }) => {
  if (matches.length === 0) return null
  const names = Array.from(new Set(matches.map((m) => m.policyName))).sort((a, b) => a.localeCompare(b))
  return (
    <Tooltip title={`Granted by polic${names.length > 1 ? 'ies' : 'y'}: ${names.join(', ')}`}>
      <Box
        component="span"
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: 'secondary.main',
          pointerEvents: 'auto',
        }}
      />
    </Tooltip>
  )
}

export const CheckboxWithPolicyBadge = ({ matches, children }: { matches: PolicyMatch[]; children: ReactNode }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
    {children}
    <PolicyInfoBadge matches={matches} />
  </Box>
)

const describePolicyPermission = (permission: Permission): string => {
  if (permission instanceof EntityPathPermission) return `Assignment → ${permission.path.toType.toString()}`
  if (permission instanceof EntitySelectorPermission) {
    if (EntitySelector.All.equals(permission.targets)) return 'All entities'
    if (permission.targets instanceof EntitySelector.OfType) return 'All of type'
    if (permission.targets instanceof EntitySelector.Single) return 'Specific entity'
    return permission.targets.toString()
  }
  return 'Global'
}

export const PolicyMatchesReadOnlyBlock = ({ matches }: { matches: PolicyMatch[] }) => {
  if (matches.length === 0) return null
  return (
    <Box
      sx={{
        border: 1,
        borderStyle: 'dashed',
        borderColor: 'primary.main',
        borderRadius: 1,
        p: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <InfoOutlined sx={{ fontSize: 14, color: 'primary.main' }} />
        <Typography variant="caption" color="text.secondary">
          Granted by {matches.length === 1 ? 'policy' : 'policies'} (read-only)
        </Typography>
      </Stack>
      <Stack spacing={0.25}>
        {matches.map((m, i) => (
          <Typography key={i} variant="body2" color="text.secondary">
            <Box component="span" sx={{ fontWeight: 600 }}>
              {m.policyName}
            </Box>
            {' — '}
            {describePolicyPermission(m.permission)}
          </Typography>
        ))}
      </Stack>
    </Box>
  )
}
